import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { type DataNode } from 'antd/es/tree'
import {
  csx,
  FilterFooter,
  FilterPopover,
  Search,
  Stack,
  useFilterMultipleState,
  useSearchState,
} from '@vtex/admin-ui'
import { BaseFilter } from '@/components/filters/BaseFilter/BaseFilter'
import { useLocalStorage } from '@/hooks/useLocalStorage'

import { CategoryTree } from '../../components/CategoryTree/CategoryTree'
import { FilterCategoryStateProvider } from '../contexts/FilterCategory'

import * as styles from '../../products-listing.styles'

interface FilterCategoryProps {
  state: ReturnType<typeof useFilterCategoryState>
}

function FilterCategoryPopover(props: FilterCategoryProps) {
  const { state } = props
  const [activeItems, setActiveItems] = useState<DataNode[]>([])

  const {
    getInputProps,
    debouncedValue: debouncedCategoryName,
    setValue,
  } = useSearchState({
    timeout: 500,
  })

  const filterState = {
    ...state,
    onClear: () => {
      setActiveItems([])
      setValue('')
      setTimeout(state.onClear, 0)
    },
  }

  useEffect(() => {
    setActiveItems(state.appliedItems)
  }, [state.appliedItems])

  return (
    <FilterPopover
      state={filterState as any}
      className={csx(styles.categoryFilterPopover)}
    >
      <Stack className={csx(styles.categoryFilterHeader)}>
        <Search {...getInputProps()} />
      </Stack>
      <FilterCategoryStateProvider state={state}>
        <Stack className={csx(styles.categoryFilterList)}>
          {state.menu?.mounted && (
            <CategoryTree
              categoryName={debouncedCategoryName}
              onChange={setActiveItems}
              initialCheckedKeys={
                activeItems.map((item) => item.key) as number[]
              }
            />
          )}
        </Stack>
      </FilterCategoryStateProvider>

      <FilterFooter
        className={csx(styles.categoryFilterFooter)}
        onClick={() =>
          filterState.onChange(
            activeItems.map((item) => ({
              ...item,
              label: item.title,
            }))
          )
        }
      />
    </FilterPopover>
  )
}

export type SerializedData = number[]

export function FilterCategory(props: { name: string }) {
  const { formatMessage } = useIntl()

  return (
    <BaseFilter
      filterName={props.name}
      useFilterState={useFilterCategoryState}
      FilterPopoverComponent={FilterCategoryPopover}
      disclosureMessage={formatMessage({
        id: 'admin/admin.catalog.category-filter-label',
        defaultMessage: 'Category',
      })}
      toSerializableData={(data) =>
        ((data.appliedItems?.map((item: any) => item.key) as number[]) ??
          []) as SerializedData
      }
      keepMounted
    />
  )
}

function useFilterCategoryState(key: string) {
  const [appliedItems, setAppliedItems] = useLocalStorage<DataNode[]>(key, [])

  const { status, menu, setStatus, ...restOfProps } = useFilterMultipleState()

  function onChange(selectedItems: DataNode[]) {
    setAppliedItems(selectedItems as any)
    menu.hide()
  }

  function onClear() {
    setAppliedItems([])
    menu.hide()
  }

  return {
    ...restOfProps,
    onClear,
    onChange,
    status,
    setStatus,
    menu,
    appliedItems,
    hasFilterValueApplied: !!appliedItems?.length,
  } as any
}
